package com.tregz.tremenz.ui.screen

import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.wrapContentWidth
import androidx.compose.foundation.layout.widthIn
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.lazy.grid.GridCells
import androidx.compose.foundation.lazy.grid.GridItemSpan
import androidx.compose.foundation.lazy.grid.LazyVerticalGrid
import androidx.compose.foundation.lazy.grid.items
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.Card
import androidx.compose.material3.CardDefaults
import androidx.compose.material3.CircularProgressIndicator
import androidx.compose.material3.Icon
import androidx.compose.material3.MaterialTheme
import androidx.compose.foundation.selection.toggleable
import androidx.compose.ui.semantics.Role
import androidx.compose.material3.Switch
import androidx.compose.material3.SwitchDefaults
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableIntStateOf
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.produceState
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberUpdatedState
import androidx.compose.runtime.saveable.rememberSaveable
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import com.tregz.tremenz.io.model.ModelChannel
import com.tregz.tremenz.io.model.ModelData
import com.tregz.tremenz.io.model.ModelRepository
import com.tregz.tremenz.io.model.ModelStatus
import com.tregz.tremenz.io.model.currentLocalHour
import com.tregz.tremenz.ui.component.StatusNotice
import com.tregz.tremenz.ui.icon.IconKey
import com.tregz.tremenz.ui.icon.iconPainter
import com.tregz.tremenz.ui.label.LabelKey
import com.tregz.tremenz.ui.label.labelText
import kotlin.time.Duration.Companion.milliseconds
import kotlin.time.Duration.Companion.seconds
import kotlin.time.TimeMark
import kotlin.time.TimeSource
import kotlinx.coroutines.delay

/** How often the local hour is read again, so a clock, timezone or DST change shows up within seconds. */
private val HOUR_POLL = 30.seconds

/** Taps closer together than this count as one, so a double tap cannot start a channel twice. */
private val TAP_DEBOUNCE = 400.milliseconds

/**
 * The Channels destination: Apero, Bistro and Club, each playing whatever `music_videos.json` has
 * scheduled for the current local hour, back to back. Starting one points the shared playback queue
 * at that hour's songs, so the host's auto-advance walks the channel instead of the whole catalogue.
 */
@Composable
fun ScreenChannels(
    currentMedia: ModelData.Media?,
    isSongPlaying: Boolean,
    isPreparingSong: Boolean,
    onPlayMedia: (ModelData.Media) -> Unit,
    modifier: Modifier = Modifier
) {
    // Observed rather than read once: on Android the programming is parsed off the main thread, so
    // the tab can be on screen before it arrives.
    val channels by ModelRepository.CHANNELS_LIVE.collectAsState()
    val status by ModelRepository.CHANNELS_STATUS.collectAsState()
    val activeChannel by ModelRepository.ACTIVE_CHANNEL.collectAsState()

    var startFromBeginning by rememberSaveable { mutableStateOf(false) }

    // Ticks rather than being read per recomposition, so the counts follow the clock.
    val hour by produceState(currentLocalHour()) {
        while (true) {
            delay(HOUR_POLL)
            value = currentLocalHour()
        }
    }
    // Resolved once per input change: songsAt scans up to 24 hours of buckets per channel.
    val scheduledByChannel = remember(channels, hour, startFromBeginning) {
        ModelChannel.KEYS.associateWith { ModelChannel.songsAt(channels, it, hour, startFromBeginning) }
    }

    // When the hour turns over while a channel is on air, its queue moves to the new hour's songs.
    var resolvedHour by remember { mutableIntStateOf(hour) }
    LaunchedEffect(hour) {
        if (hour != resolvedHour) {
            resolvedHour = hour
            val channel = ModelRepository.ACTIVE_CHANNEL.value
            val fresh = channel?.let { scheduledByChannel[it] }
            if (!startFromBeginning && channel != null && !fresh.isNullOrEmpty()) {
                ModelRepository.setActiveSongs(fresh, channel)
            }
        }
    }

    // Read inside the click, not captured: a tap lands before the next recomposition.
    val latestMedia by rememberUpdatedState(currentMedia)
    var lastTap by remember { mutableStateOf<TimeMark?>(null) }

    // Cards stay one per row until the pane can hold two of them side by side.
    LazyVerticalGrid(
        columns = GridCells.Adaptive(minSize = 400.dp),
        modifier = modifier
            .fillMaxWidth()
            .wrapContentWidth(Alignment.CenterHorizontally)
            .widthIn(max = 1200.dp)
    ) {
        item(span = { GridItemSpan(maxLineSpan) }) {
            Row(
                modifier = Modifier
                    .fillMaxWidth()
                    .toggleable(
                        value = startFromBeginning,
                        role = Role.Switch,
                        onValueChange = { startFromBeginning = it }
                    )
                    .padding(horizontal = 16.dp, vertical = 8.dp),
                verticalAlignment = Alignment.CenterVertically,
                horizontalArrangement = Arrangement.SpaceBetween
            ) {
                Text(
                    text = labelText(LabelKey.CONTROL_START_FROM_BEGINNING),
                    color = MaterialTheme.colorScheme.onBackground,
                    style = MaterialTheme.typography.titleMedium,
                    fontWeight = FontWeight.SemiBold
                )
                Switch(
                    checked = startFromBeginning,
                    onCheckedChange = null,
                    colors = SwitchDefaults.colors(
                        checkedThumbColor = MaterialTheme.colorScheme.onPrimary,
                        checkedTrackColor = MaterialTheme.colorScheme.primary
                    )
                )
            }
        }

        // Until the programming arrives every card would read "nothing scheduled", which is false.
        if (channels.isEmpty()) {
            item(span = { GridItemSpan(maxLineSpan) }) {
                StatusNotice(status = status, onRetry = ModelRepository::retry)
            }
        }

        items(ModelChannel.KEYS, key = { it }) { channel ->
            val scheduled = scheduledByChannel[channel].orEmpty()
            val isOnAir = activeChannel == channel
            CardChannel(
                label = labelText(channelLabel(channel)),
                icon = channelIcon(channel),
                count = scheduled.size,
                offAirLabel = when {
                    channels.isNotEmpty() || status == ModelStatus.LOADED -> LabelKey.CHANNEL_OFF_AIR
                    status == ModelStatus.FAILED -> LabelKey.STATUS_FAILED
                    else -> LabelKey.STATUS_LOADING
                },
                isPlaying = isOnAir && isSongPlaying,
                isPreparing = isOnAir && isPreparingSong,
                onPlayClick = {
                    val previous = lastTap
                    if (previous == null || previous.elapsedNow() >= TAP_DEBOUNCE) {
                        lastTap = TimeSource.Monotonic.markNow()
                        val playing = latestMedia
                        if (ModelRepository.ACTIVE_CHANNEL.value == channel && playing is ModelData.Song) {
                            // Already on air: hand the host the song it is holding so its own
                            // play/pause toggle runs, rather than restarting the channel. While
                            // it is still tuning in there is nothing to toggle.
                            if (!isPreparingSong) onPlayMedia(playing)
                        } else if (scheduled.isNotEmpty()) {
                            ModelRepository.setActiveSongs(scheduled, channel)
                            onPlayMedia(scheduled.first())
                        }
                    }
                }
            )
        }
    }
}

/** One channel row: its name, how much it has on air this hour, and a play/pause button. */
@Composable
private fun CardChannel(
    label: String,
    icon: IconKey,
    count: Int,
    offAirLabel: LabelKey,
    isPlaying: Boolean,
    isPreparing: Boolean,
    onPlayClick: () -> Unit
) {
    val scheme = MaterialTheme.colorScheme
    val isOffAir = count == 0

    Card(
        onClick = onPlayClick,
        enabled = !isOffAir,
        modifier = Modifier
            .fillMaxWidth()
            .padding(horizontal = 12.dp, vertical = 6.dp),
        colors = CardDefaults.cardColors(
            containerColor = scheme.surfaceContainer
        ),
        shape = RoundedCornerShape(8.dp)
    ) {
        Row(
            modifier = Modifier
                .fillMaxWidth()
                .padding(12.dp),
            verticalAlignment = Alignment.CenterVertically
        ) {
            Icon(
                painter = iconPainter(icon),
                contentDescription = label,
                tint = scheme.primary,
                modifier = Modifier.size(48.dp)
            )

            Spacer(modifier = Modifier.width(12.dp))

            Column(modifier = Modifier.weight(1f)) {
                Text(
                    text = label,
                    color = if (isPlaying) scheme.primary else scheme.onSurface,
                    style = MaterialTheme.typography.titleMedium
                )
                Text(
                    text = when {
                        isOffAir -> labelText(offAirLabel)
                        isPreparing -> labelText(LabelKey.CHANNEL_TUNING_IN)
                        else -> "$count ${labelText(LabelKey.CHANNEL_ON_AIR_SUFFIX)}"
                    },
                    color = scheme.onSurfaceVariant,
                    style = MaterialTheme.typography.bodySmall
                )
            }

            if (isPreparing) {
                CircularProgressIndicator(
                    color = scheme.primary,
                    modifier = Modifier.size(24.dp),
                    strokeWidth = 2.5.dp
                )
            }
        }
    }
}

private fun channelLabel(channel: String): LabelKey = when (channel) {
    ModelChannel.BISTRO -> LabelKey.CHANNEL_BISTRO
    ModelChannel.CLUB -> LabelKey.CHANNEL_CLUB
    else -> LabelKey.CHANNEL_APERO
}

private fun channelIcon(channel: String): IconKey = when (channel) {
    ModelChannel.BISTRO -> IconKey.BEER
    ModelChannel.CLUB -> IconKey.COCKTAIL
    else -> IconKey.WINE
}
